import React, { useEffect, useMemo, useRef, useState } from "react";

const OPEN_KEY = "EmitterTool:CollisionPanelOpenV3";
const COLL_THUMB_KEY = "EmitterTool:CollisionThumbSize";
const COLL_SHOW_NAMES_KEY = "EmitterTool:CollisionShowNames";
const COLL_FILTER_KEY = "EmitterTool:CollisionSpriteFilter";

function CollapsibleBox({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(OPEN_KEY);
      return saved == null ? defaultOpen : saved === "1";
    } catch {
      return defaultOpen;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_KEY, open ? "1" : "0");
    } catch {}
  }, [open]);

  return (
    <div
      style={{
        border: "1px solid #334155",
        borderRadius: 8,
        marginBottom: 12,
        background: "#0b0f17",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          padding: "8px 10px",
          background: "#18212f",
          userSelect: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 600,
          gap: 8,
        }}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </span>
        <span style={{ opacity: 0.75, flex: "0 0 auto" }}>
          {open ? "▾" : "▸"}
        </span>
      </div>
      {open && <div style={{ padding: 10 }}>{children}</div>}
    </div>
  );
}

const labelStyle = { fontSize: 12, opacity: 0.85, overflowWrap: "anywhere" };
const rowStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
  gap: 8,
  marginBottom: 8,
};
const selectStyle = {
  width: "100%",
  maxWidth: "100%",
  background: "#0b0d12",
  color: "white",
  border: "1px solid #374151",
  borderRadius: 8,
  padding: "6px 8px",
  fontSize: 12,
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  cursor: "pointer",
  boxSizing: "border-box",
  display: "block",
};
const inputStyle = {
  width: "100%",
  maxWidth: "100%",
  background: "#0b0d12",
  color: "white",
  border: "1px solid #374151",
  borderRadius: 8,
  padding: "6px 8px",
  fontSize: 12,
  boxSizing: "border-box",
  display: "block",
};
const sliderStyle = {
  width: "100%",
  accentColor: "#64748b",
  cursor: "pointer",
};

function SpriteThumb({ texture, size = 72 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!texture) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const frame =
      texture.frame ||
      texture.orig || { x: 0, y: 0, width: texture.width, height: texture.height };

    const source =
      texture.baseTexture?.resource?.source ||
      texture.baseTexture?.resource?.src ||
      texture.baseTexture?.resource?.url ||
      texture.baseTexture?.resource?.sourceElement ||
      null;

    const ctx = canvas.getContext("2d");
    const w = Math.max(1, frame.width || texture.width || 1);
    const h = Math.max(1, frame.height || texture.height || 1);

    canvas.width = size;
    canvas.height = size;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const cell = 6;
    for (let y = 0; y < size; y += cell) {
      for (let x = 0; x < size; x += cell) {
        ctx.fillStyle = (Math.floor(x / cell + y / cell) % 2 === 0) ? "#0e141f" : "#101826";
        ctx.fillRect(x, y, cell, cell);
      }
    }

    if (!source || !source.width || !source.height) return;

    const scale = Math.min(size / w, size / h);
    const drawW = Math.max(1, Math.floor(w * scale));
    const drawH = Math.max(1, Math.floor(h * scale));
    const dx = Math.floor((size - drawW) / 2);
    const dy = Math.floor((size - drawH) / 2);

    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(
      source,
      frame.x || 0,
      frame.y || 0,
      w,
      h,
      dx,
      dy,
      drawW,
      drawH
    );
  }, [texture, size]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{
        width: size,
        height: size,
        borderRadius: 6,
        display: "block",
        background: "#0f172a",
      }}
    />
  );
}

function SingleSpritePicker({
  atlas,
  value,
  onChange,
  title = "Collision Sprite",
  defaultOpen = true,
  disabled = false,
}) {
  const computedAtlas = atlas || null;

  const [filter, setFilter] = useState(() => {
    try { return localStorage.getItem(COLL_FILTER_KEY) || ""; } catch { return ""; }
  });
  const [showNames, setShowNames] = useState(() => {
    try {
      const raw = localStorage.getItem(COLL_SHOW_NAMES_KEY);
      return raw == null ? true : raw === "1";
    } catch {
      return true;
    }
  });
  const [thumbSize, setThumbSize] = useState(() => {
    try {
      const raw = localStorage.getItem(COLL_THUMB_KEY);
      const n = raw ? Number(raw) : 72;
      return Number.isFinite(n) ? Math.min(160, Math.max(32, n)) : 72;
    } catch {
      return 72;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLL_FILTER_KEY, filter);
    } catch {}
  }, [filter]);
  useEffect(() => {
    try {
      localStorage.setItem(COLL_SHOW_NAMES_KEY, showNames ? "1" : "0");
    } catch {}
  }, [showNames]);
  useEffect(() => {
    try {
      localStorage.setItem(COLL_THUMB_KEY, String(thumbSize));
    } catch {}
  }, [thumbSize]);

  const frameNames = computedAtlas?.frameNames || [];
  const textures = computedAtlas?.textures || {};

  const filteredNames = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return frameNames;
    return frameNames.filter((name) => name.toLowerCase().includes(q));
  }, [frameNames, filter]);

  const count = filteredNames.length;

  return (
    <CollapsibleBox title={title} defaultOpen={defaultOpen}>
      <fieldset
        disabled={disabled}
        style={{
          border: "none",
          padding: 0,
          margin: 0,
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 8,
            marginBottom: 8,
            alignItems: "center",
            boxSizing: "border-box",
            maxWidth: "100%",
          }}
        >
          <input
            type="text"
            placeholder="Search sprites…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={inputStyle}
          />
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              whiteSpace: "nowrap",
            }}
          >
            <input
              type="checkbox"
              checked={showNames}
              onChange={(e) => setShowNames(e.target.checked)}
              style={{ accentColor: "#64748b" }}
            />
            <span style={{ fontSize: 12 }}>Show names</span>
          </label>
        </div>

        <div style={{ marginBottom: 10 }}>
          <div
            style={{
              ...labelStyle,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>Thumbnail size</span>
            <span style={{ opacity: 0.7 }}>{thumbSize}px</span>
          </div>
          <input
            type="range"
            min={32}
            max={160}
            step={1}
            value={thumbSize}
            onChange={(e) => setThumbSize(Number(e.target.value))}
            style={sliderStyle}
          />
        </div>

        <div
          style={{
            fontSize: 12,
            opacity: 0.75,
            marginBottom: 8,
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
        >
          {computedAtlas
            ? `${count} / ${frameNames.length} sprites — single-select`
            : "Spritesheet not loaded"}
        </div>

        {computedAtlas && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(auto-fill, minmax(${
                thumbSize + 16
              }px, 1fr))`,
              gap: 8,
              maxHeight: 360,
              overflow: "auto",
              overflowX: "hidden",
              paddingRight: 2,
              boxSizing: "border-box",
              maxWidth: "100%",
            }}
          >
            {filteredNames.map((name) => {
              const tex = textures[name];
              const isSelected = value === name;
              const onClick = () => onChange?.(name);

              return (
                <button
                  key={name}
                  title={name}
                  onClick={onClick}
                  style={{
                    position: "relative",
                    border: isSelected
                      ? "2px solid #60a5fa"
                      : "1px solid #374151",
                    outline: "none",
                    background: isSelected ? "#0b1220" : "transparent",
                    borderRadius: 8,
                    padding: 6,
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    alignItems: "center",
                    cursor: "pointer",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <SpriteThumb texture={tex} size={thumbSize} />
                  {showNames && (
                    <div
                      style={{
                        width: "100%",
                        fontSize: 11,
                        lineHeight: 1.2,
                        textAlign: "center",
                        color: "#cbd5e1",
                        wordBreak: "break-word",
                      }}
                    >
                      {name}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {value && (
          <div
            style={{
              marginTop: 10,
              paddingTop: 10,
              borderTop: "1px dashed #334155",
              fontSize: 12,
              color: "#cbd5e1",
            }}
          >
            <div style={{ marginBottom: 4, fontWeight: 600 }}>Selected</div>
            <div style={{ opacity: 0.85 }}>{value}</div>
          </div>
        )}
      </fieldset>
    </CollapsibleBox>
  );
}

export default function CollisionPanel({
  atlas,
  effectSettings = {},
  setEffectSettings,
  onSettingsChange,
  title = "Collision",
  defaultOpen = false,
}) {
  const callOnSettingsChange =
    typeof onSettingsChange === "function" ? onSettingsChange : null;

  const broadcast = (settings) => {
    try {
      window.dispatchEvent(
        new CustomEvent("EmitterTool:CollisionSettings", { detail: settings })
      );
    } catch {}
  };

  const setES = (patch) => {
    const apply = (base) =>
      typeof patch === "function" ? patch(base) : { ...base, ...patch };
    if (typeof setEffectSettings === "function") {
      setEffectSettings((cur) => {
        const base = cur || {};
        const next = apply(base);
        if (callOnSettingsChange) {
          try {
            callOnSettingsChange(next);
          } catch {}
        }
        broadcast(next);
        return next;
      });
    } else {
      const base = effectSettings || {};
      const next = apply(base);
      if (callOnSettingsChange) {
        try {
          callOnSettingsChange(next);
        } catch {}
      }
      broadcast(next);
    }
  };

  const {
    useWalls = false,
    wallResponse = "stop",
    useTokenCollision = false,
    tokenResponse = "hardBlock",

    collisionEffectEnabled = false,
    collisionSprite = "",
    collisionScale = 1,
    collisionAlpha = 1,
    collisionLifespanMs = 400,
    collisionFxRotationMode = "none",
  } = effectSettings || {};

  const wallResponseOptions = [
    { value: "stop", label: "Stop" },
    { value: "travel", label: "Travel (slide along)" },
    { value: "bounce", label: "Bounce" },
    { value: "cling", label: "Cling (wrap along)" },
    { value: "kill", label: "Kill (die on impact)" },
  ];

  const tokenResponseOptions = [
    { value: "hardBlock", label: "Block (hard stop)" },
    { value: "flowAround", label: "Flow around (stream past body)" },
    { value: "softObstruction", label: "Soft obstruction (some through)" },
    { value: "coreAura", label: "Core + aura (2-radius)" },
    { value: "facingAware", label: "Facing-aware (front vs back)" },
  ];

  const rotationOptions = [
    { value: "none", label: "No rotation" },
    { value: "random", label: "Random" },
    { value: "normal", label: "Align to surface normal" },
    { value: "tangent", label: "Align to surface tangent" },
  ];

  return (
    <CollapsibleBox title={title} defaultOpen={defaultOpen}>
      <div style={{ maxWidth: "100%", overflowX: "hidden" }}>
        <div style={rowStyle}>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={!!useWalls}
              onChange={(e) => setES({ useWalls: e.target.checked })}
              style={{ accentColor: "#64748b" }}
            />
            <span style={{ fontSize: 12 }}>Collide with walls</span>
          </label>

          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={!!useTokenCollision}
              onChange={(e) => setES({ useTokenCollision: e.target.checked })}
              style={{ accentColor: "#64748b" }}
            />
            <span style={{ fontSize: 12 }}>Collide with token</span>
          </label>
        </div>

        <div style={rowStyle}>
          <div style={{ minWidth: 0 }}>
            <div style={labelStyle}>Wall response</div>
            <select
              value={wallResponse}
              onChange={(e) => setES({ wallResponse: e.target.value })}
              style={selectStyle}
              disabled={!useWalls}
            >
              {wallResponseOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ minWidth: 0 }}>
            <div style={labelStyle}>Token response</div>
            <select
              value={tokenResponse}
              onChange={(e) => setES({ tokenResponse: e.target.value })}
              style={selectStyle}
              disabled={!useTokenCollision}
            >
              {tokenResponseOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div
          style={{
            borderTop: "1px solid #1f2937",
            marginTop: 10,
            paddingTop: 10,
          }}
        >
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={!!collisionEffectEnabled}
              onChange={(e) =>
                setES({ collisionEffectEnabled: e.target.checked })
              }
              style={{ accentColor: "#64748b" }}
            />
            <span style={{ fontSize: 12 }}>Spawn collision effect</span>
          </label>

          {collisionEffectEnabled && (
            <div style={{ marginTop: 8 }}>
              <SingleSpritePicker
                atlas={atlas}
                value={collisionSprite}
                onChange={(name) => setES({ collisionSprite: name })}
                title="Collision Sprite"
                defaultOpen={true}
              />

              <div style={rowStyle}>
                <div style={{ minWidth: 0 }}>
                  <div style={labelStyle}>Rotation</div>
                  <select
                    value={collisionFxRotationMode}
                    onChange={(e) =>
                      setES({ collisionFxRotationMode: e.target.value })
                    }
                    style={selectStyle}
                  >
                    {rotationOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ minWidth: 0 }} />
              </div>

              <div style={rowStyle}>
                <div style={{ minWidth: 0 }}>
                  <div style={labelStyle}>Scale</div>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={Number.isFinite(collisionScale) ? collisionScale : 1}
                    onChange={(e) =>
                      setES({
                        collisionScale: Math.max(
                          0,
                          parseFloat(e.target.value || "0")
                        ),
                      })
                    }
                    style={inputStyle}
                  />
                </div>

                <div style={{ minWidth: 0 }}>
                  <div style={labelStyle}>Alpha</div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={Number.isFinite(collisionAlpha) ? collisionAlpha : 1}
                    onChange={(e) =>
                      setES({ collisionAlpha: parseFloat(e.target.value) })
                    }
                    style={{ width: "100%" }}
                  />
                  <div
                    style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}
                  >
                    {Math.round((collisionAlpha ?? 1) * 100)}%
                  </div>
                </div>
              </div>

              <div style={rowStyle}>
                <div style={{ minWidth: 0 }}>
                  <div style={labelStyle}>Lifespan (ms)</div>
                  <input
                    type="number"
                    min="0"
                    step="50"
                    value={
                      Number.isFinite(collisionLifespanMs)
                        ? collisionLifespanMs
                        : 400
                    }
                    onChange={(e) =>
                      setES({
                        collisionLifespanMs: Math.max(
                          0,
                          parseInt(e.target.value || "0", 10)
                        ),
                      })
                    }
                    style={inputStyle}
                  />
                </div>
                <div style={{ minWidth: 0 }} />
              </div>
            </div>
          )}
        </div>

        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 6 }}>
          These settings define <em>behaviour on hit</em>. Rendering walls and
          the “Token enabled” toggle are managed elsewhere in your UI.
        </div>
      </div>
    </CollapsibleBox>
  );
}

export { CollisionPanel };
